import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  AddOnItem,
  AddOnItemName,
  BentoCategory,
  OrganizationVaProfile,
  SearchParams,
} from "schemas/dashboard";
import { Map } from "schemas/functions";

import Alert from "components/Alert";
import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useAlert } from "utils/useAlert";

import AboutForm from "../Onboarding/AboutForm";
import { getUserType } from "../Onboarding/AboutForm/helpers";
import SelfServeVaComplete from "./Complete";
import NichePick from "./NichePick";
import TemplatesPick from "./TemplatesPick";
import Welcome from "./Welcome";
import {
  getQuantityForItem,
  redirectToStep,
  vaSelfServeNavigate,
} from "./helpers";
import styles from "./styles";

const SelfServeVaOnboarding = () => {
  const {
    currentOrg,
    setCurrentUser,
    setCurrentOrg,
    setUserNotifications,
    setUserIntegrations,
    setEmailSettings,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const theme = useTheme();
  const slideContext = useContext(SlideAnimationContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const { slideInUp, slideProps } = useContext(SlideAnimationContext);
  const { fetchOutreachTemplates, templates } = useContext(
    OutreachTemplatesContext,
  );
  const [message, severity, setAlert, closeAlert] = useAlert();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const source = getSource();
  const [bundledAddOns, setBundledAddOns] = useState<AddOnItem[]>([]);

  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const numOfNiche =
    getQuantityForItem(AddOnItemName.niche, bundledAddOns) || 0;
  const numOfTemplates =
    getQuantityForItem(AddOnItemName.pitching_templates, bundledAddOns) || 0;
  const numOfCall =
    getQuantityForItem(AddOnItemName.support_call, bundledAddOns) || 0;

  const [organizationVaProfile, setOrganizationVaProfile] =
    useState<OrganizationVaProfile | null>(null);
  const [categoriesToPickFrom, setCategoriesToPickFrom] = useState<
    BentoCategory[]
  >([]);
  const [onboardingForm, setOnboardingForm] = useState<Map>({});

  const fetchUserInfo = async () => {
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      setFetchUserLoading(false);
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setSubscription(res.subscription);
      setUserIntegrations(res.userIntegrations);
      setUserNotifications(res.userNotifications);
      setEmailSettings(res.emailSetting);
      setOnboardingForm((prev) => ({
        ...prev,
        ...res.organizationProfile,
        name: res.organizationUser.name,
        pitchEmail: res.organizationProfile?.pitchEmail,
        userType: getUserType(res.organizationProfile),
      }));
      setIncludedBentoSearchFilterIds(res.includedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(res.excludedBentoSearchFilterIds);
      setOrganizationVaProfile(res.organizationVaProfile);
      slideInUp();

      const routeToReturnTo = redirectToStep(
        res.organization,
        res.organizationVaProfile,
        res.organizationProfile,
        window.location.href,
      );
      navigate(`/${routeToReturnTo}${location.search}`);
    } catch (error) {}
  };

  const getCategoriesToPickFrom = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-categories/onboarding?for_va=${true}`,
      );
      setCategoriesToPickFrom(res.onboardingBentoCategories);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const fetchBundledItems = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons/va-bundled-items`,
      );
      setBundledAddOns(res.addOnItems);
    } catch (error) {
      setAlert(
        error?.message ||
          "Something went wrong. Please refresh and retry again",
        "error",
      );
    }
  };

  const saveOrgVaProfile = async (
    orgVaProfile: OrganizationVaProfile | null,
    excludeIncludeFilterHasChanged?: boolean,
    moveNext = true,
  ) => {
    setLoading(true);

    if (excludeIncludeFilterHasChanged) {
      handleSaveFilters();
    }
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/va-profile`,
        "PUT",
        {},
        { ...orgVaProfile, page: location.pathname, source },
      );
      setOrganizationVaProfile(res.organizationVaProfile);

      if (moveNext) {
        if (slideContext) {
          await slideContext.slideOutUp();
          slideContext.slideInUp();
        }
        navigate(
          `/${vaSelfServeNavigate(location.pathname, "next")}${location.search}`,
        );
      }
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const saveOrgProfile = async (onboardingForm: Map, moveNext = true) => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...onboardingForm,
          page: location.pathname,
          source,
          isVaFlow: true,
        },
      );
      if (moveNext) {
        if (slideContext) {
          await slideContext.slideOutUp();
          slideContext.slideInUp();
        }
        navigate(
          `/${vaSelfServeNavigate(location.pathname, "next")}${location.search}`,
        );
      }
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAbout = async () => {
    await saveOrgProfile(onboardingForm);

    if (slideContext) {
      await slideContext.slideOutUp();
      slideContext.slideInUp();
    }
    navigate(
      `/${vaSelfServeNavigate(location.pathname, "next")}${location.search}`,
    );
  };

  const handleSaveFilters = async () => {
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/search-filters`,
        "PUT",
        {},
        {
          include: includedBentoSearchFilterIds,
          exclude: excludedBentoSearchFilterIds,
        },
      );
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  useEffect(() => {
    fetchUserInfo();
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Fetch outreach templates to store for default pitch email.
    if (currentOrg?.id && templates?.length === 0) {
      fetchOutreachTemplates();
    }
    if (currentOrg?.id) {
      getCategoriesToPickFrom();
      fetchBundledItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <>
      <Grid container sx={styles.root}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid sx={styles.formContainer} item xs={12}>
            <Routes>
              <Route path="/welcome" element={<Welcome />} />
              <Route
                path="/about"
                element={
                  <AboutForm
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    handleNext={handleSubmitAbout}
                    hideDiscoveryQuestion={Boolean(referralCode)}
                    isVAFlow
                  />
                }
              />
              <Route
                path="/niches"
                element={
                  <NichePick
                    loading={loading}
                    categoriesToPickFrom={categoriesToPickFrom}
                    numOfNiche={numOfNiche}
                    saveOrgVaProfile={saveOrgVaProfile}
                    organizationVaProfile={organizationVaProfile}
                    setOrganizationVaProfile={setOrganizationVaProfile}
                  />
                }
              />
              <Route
                path="/templates"
                element={
                  <TemplatesPick
                    organizationVaProfile={organizationVaProfile}
                    saveOrgVaProfile={saveOrgVaProfile}
                    numOfTemplates={numOfTemplates}
                    setOrganizationVaProfile={setOrganizationVaProfile}
                    onboardingForm={onboardingForm}
                    setOnboardingForm={setOnboardingForm}
                    saveOrgProfile={saveOrgProfile}
                    loading={loading}
                  />
                }
              />
              <Route
                path="/faq"
                element={
                  <SelfServeVaComplete
                    boughtTemplates={numOfTemplates > 0}
                    organizationVaProfile={organizationVaProfile}
                    saveOrgVaProfile={saveOrgVaProfile}
                    bookedCall={numOfCall > 0}
                    loading={loading}
                  />
                }
              />
            </Routes>
          </Grid>
        </Slide>
      </Grid>

      <Alert message={message} severity={severity} closeAlert={closeAlert} />
    </>
  );
};

export default function VAOnboarding() {
  return (
    <SlideAnimationProvider>
      <SelfServeVaOnboarding />
    </SlideAnimationProvider>
  );
}
